import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, X } from 'lucide-react';
import {
  CheckCircle,
  AlertTriangle,
  Download,
  FileText,
  Loader2,
  Save,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardX,
  Search,
  CheckSquare,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentConfig } from '@/hooks/useDocumentConfig';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { PdfDocumentBuilder, type EmpresaInfo, type DocumentConfig } from '@/lib/pdfDocumentEngine';
import SignatureCanvas from 'react-signature-canvas';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { renderCurvaCaudalToDataURL } from '@/lib/checklists/curvaCaudalChart';
import { resolveInstrumentosGrupoPresion } from '@/lib/checklists/instrumentosGrupoPresion';

interface Sistema {
  id: string;
  tipo: string;
  categoria: string;
  descripcion?: string;
  ubicacion?: string;
}

interface ChecklistResumen {
  id: string;
  sistema_id: string;
  estado: string; // en_progreso | completado
  fecha: string;
  tipo_revision: string;
}

interface ElementoMin {
  id: string;
  nombre: string;
  tipo: string;
  zona: string | null;
  revisado: boolean;
  sistema_id: string;
  observaciones?: string | null;
  numero_elemento?: number | null;
  estado?: string | null;
  fecha_instalacion?: string | null;
  tipo_equipo?: string | null;
  marca?: string | null;
  modelo?: string | null;
  vr?: string | null;
  area_particion?: string | null;
  mac_serie?: string | null;
  codigo_instalador?: string | null;
  anio_fabricacion?: string | null;
  datos_tecnicos?: Record<string, any> | null;
}

interface AnomaliaRow {
  id: string;
  elemento_id: string;
  codigo_anomalia: string;
  descripcion: string | null;
  notas_resolucion?: string | null;
  resuelta: boolean;
}

interface CodigoAnomaliaInfo {
  codigo: string;
  descripcion: string | null;
  severidad: string | null;
  solucion_sugerida: string | null;
  referencia_normativa?: string | null;
  justificacion_normativa?: string | null;
}

interface InformeMultiSistemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sistemas: Sistema[];
  clienteNombre?: string;
  clienteId?: string;
  sede?: { id?: string; nombre: string; direccion?: string | null; codigo_postal?: string | null; poblacion?: string | null; provincia?: string | null } | null;
  docConfig?: Partial<DocumentConfig>;
  /** Reanudar un borrador concreto */
  informeId?: string;
  /** Categoría a la que se acota el informe (Seguridad o PCI). Si se reanuda un borrador, se lee de la BD. */
  categoria?: 'seguridad' | 'pci';
}

const CATEGORIA_LABEL: Record<'seguridad' | 'pci', string> = {
  seguridad: 'Seguridad',
  pci: 'PCI (Protección Contra Incendios)',
};

const extractReadableObservaciones = (raw: string): string[] => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return [];

  const pickText = (value: any): string[] => {
    if (!value) return [];
    if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
    if (Array.isArray(value)) return value.flatMap(pickText);
    if (typeof value === 'object') {
      const text = value.texto || value.observacion || value.observaciones || value.descripcion || value.detalle;
      return typeof text === 'string' && text.trim() ? [text.trim()] : [];
    }
    return [];
  };

  if (/^[\[{]/.test(trimmed)) {
    try {
      const parsed = JSON.parse(trimmed);
      const readable = pickText(parsed);
      if (readable.length > 0) return readable;
    } catch {
      return [trimmed];
    }
  }

  return [trimmed];
};

type Step = 'select' | 'sign' | 'preview';

export const InformeMultiSistemaDialog: React.FC<InformeMultiSistemaDialogProps> = ({
  open,
  onOpenChange,
  sistemas,
  clienteNombre,
  clienteId,
  sede,
  docConfig: docConfigProp,
  informeId: initialInformeId,
  categoria: categoriaProp,
}) => {
  const { perfil, empresa } = useAuth();
  const { docConfig } = useDocumentConfig(docConfigProp);
  const { clientes } = useSecurityContext();
  const cliente = useMemo(() => clientes.find(c => c.id === clienteId), [clientes, clienteId]);
  const { toast } = useToast();
  const sigTecnicoRef = useRef<SignatureCanvas>(null);

  const sigClienteRef = useRef<SignatureCanvas>(null);

  const [step, setStep] = useState<Step>('select');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [informeId, setInformeId] = useState<string | undefined>(initialInformeId);
  const [estado, setEstado] = useState<'borrador' | 'finalizado'>('borrador');
  const [categoria, setCategoria] = useState<'seguridad' | 'pci'>(categoriaProp || 'seguridad');
  const [selectedSistemaIds, setSelectedSistemaIds] = useState<Set<string>>(new Set());
  const [firmaTecnico, setFirmaTecnico] = useState<string>('');
  const [firmaCliente, setFirmaCliente] = useState<string>('');
  const [nombreFirmanteCliente, setNombreFirmanteCliente] = useState<string>('');
  const [observacionesGenerales, setObservacionesGenerales] = useState<string>('');

  // Datos cargados
  const [checklistsPorSistema, setChecklistsPorSistema] = useState<Record<string, ChecklistResumen | undefined>>({});
  const [checklistsDisponiblesPorSistema, setChecklistsDisponiblesPorSistema] = useState<Record<string, ChecklistResumen[]>>({});
  const [revisionElementosPorSistema, setRevisionElementosPorSistema] = useState<Record<string, ChecklistResumen | undefined>>({});
  const [borradoresExistentes, setBorradoresExistentes] = useState<Array<{ id: string; fecha: string; sistemas_ids: string[] }>>([]);
  const [elementosPorSistema, setElementosPorSistema] = useState<Record<string, ElementoMin[]>>({});
  const [elementosProbadosPorSistema, setElementosProbadosPorSistema] = useState<Record<string, Set<string>>>({});
  const [anomaliasPorSistema, setAnomaliasPorSistema] = useState<Record<string, AnomaliaRow[]>>({});
  const [leyendaCodigos, setLeyendaCodigos] = useState<Record<string, CodigoAnomaliaInfo>>({});
  const [observacionesElemento, setObservacionesElemento] = useState<Record<string, string>>({}); // elemento_id -> texto
  const [observacionesSistema, setObservacionesSistema] = useState<Record<string, string[]>>({}); // sistema_id -> textos

  // Filtros para selección de checklists
  const [showFiltros, setShowFiltros] = useState(false);
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroTipoRevision, setFiltroTipoRevision] = useState<string>('todos');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState<string>('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState<string>('');

  // Acotar el conjunto de sistemas visibles a la categoría del informe
  const sistemasCategoria = useMemo(
    () => sistemas.filter(s => (s.categoria || 'seguridad') === categoria),
    [sistemas, categoria]
  );

  const selectedSistemas = useMemo(
    () => sistemasCategoria.filter(s => selectedSistemaIds.has(s.id)),
    [sistemasCategoria, selectedSistemaIds]
  );

  const tiposRevisionDisponibles = useMemo(() => {
    const set = new Set<string>();
    Object.values(checklistsDisponiblesPorSistema).forEach(lista => {
      (lista || []).forEach(c => { if (c.tipo_revision) set.add(c.tipo_revision); });
    });
    return Array.from(set).sort();
  }, [checklistsDisponiblesPorSistema]);

  const filtrarChecklists = (lista: ChecklistResumen[]): ChecklistResumen[] => {
    const desde = filtroFechaDesde ? new Date(filtroFechaDesde).getTime() : null;
    const hasta = filtroFechaHasta ? new Date(filtroFechaHasta).getTime() + 24 * 60 * 60 * 1000 : null;
    const q = filtroBusqueda.trim().toLowerCase();
    return lista.filter(c => {
      if (filtroEstado !== 'todos') {
        const isCompletado = c.estado === 'completado' || c.estado === 'finalizado';
        if (filtroEstado === 'completado' && !isCompletado) return false;
        if (filtroEstado === 'en_progreso' && isCompletado) return false;
      }
      if (filtroTipoRevision !== 'todos' && c.tipo_revision !== filtroTipoRevision) return false;
      if (q && !(c.tipo_revision || '').toLowerCase().includes(q)) return false;
      const t = new Date(c.fecha).getTime();
      if (desde !== null && t < desde) return false;
      if (hasta !== null && t > hasta) return false;
      return true;
    });
  };

  const reset = () => {
    setStep('select');
    setInformeId(undefined);
    setEstado('borrador');
    setSelectedSistemaIds(new Set());
    setFirmaTecnico('');
    setFirmaCliente('');
    setNombreFirmanteCliente('');
    setObservacionesGenerales('');
    setElementosPorSistema({});
    setElementosProbadosPorSistema({});
    setFiltroBusqueda('');
    setFiltroEstado('todos');
    setFiltroTipoRevision('todos');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
  };

  // ------ Carga inicial: estados de checklist por sistema + borradores existentes ------
  useEffect(() => {
    if (!open || !clienteId) return;
    // Si no se reanuda un borrador, alinear la categoría con la prop entrante
    if (!initialInformeId && categoriaProp) {
      setCategoria(categoriaProp);
    }
    (async () => {
      setLoading(true);
      const sistemaIds = sistemasCategoria.map(s => s.id);

      // Checklists (cuestionario) y revisiones de elementos más recientes por sistema
      const { data: checklists } = await supabase
        .from('checklists_seguridad')
        .select('id, sistema_id, estado, fecha, tipo_revision, modo, archivado')
        .eq('cliente_id', clienteId)
        .eq('archivado', false)
        .in('sistema_id', sistemaIds.length ? sistemaIds : ['00000000-0000-0000-0000-000000000000'])
        .order('created_at', { ascending: false });

      const mapChecklist: Record<string, ChecklistResumen> = {};
      const mapDisponibles: Record<string, ChecklistResumen[]> = {};
      const mapElementos: Record<string, ChecklistResumen> = {};
      (checklists || []).forEach((c: any) => {
        if (!c.sistema_id) return;
        const esRapida = c.modo === 'elementos' || c.tipo_revision === 'rapida';
        if (esRapida) {
          if (!mapElementos[c.sistema_id]) mapElementos[c.sistema_id] = c as ChecklistResumen;
        } else {
          (mapDisponibles[c.sistema_id] = mapDisponibles[c.sistema_id] || []).push(c as ChecklistResumen);
          // Por defecto, el más reciente completado; si no hay, el más reciente
          const prev = mapChecklist[c.sistema_id];
          if (!prev) {
            mapChecklist[c.sistema_id] = c as ChecklistResumen;
          } else if (prev.estado !== 'completado' && prev.estado !== 'finalizado' && (c.estado === 'completado' || c.estado === 'finalizado')) {
            mapChecklist[c.sistema_id] = c as ChecklistResumen;
          }
        }
      });
      setChecklistsPorSistema(mapChecklist);
      setChecklistsDisponiblesPorSistema(mapDisponibles);
      setRevisionElementosPorSistema(mapElementos);

      // Borradores existentes para este cliente
      const { data: drafts } = await supabase
        .from('informes_consolidados')
        .select('id, fecha, sistemas_ids')
        .eq('cliente_id', clienteId)
        .eq('estado', 'borrador')
        .order('created_at', { ascending: false });
      setBorradoresExistentes((drafts || []) as any);

      // Si nos pasan un id concreto, cargarlo
      if (initialInformeId) {
        await loadInforme(initialInformeId, mapDisponibles);
      }


      setLoading(false);
    })();
  }, [open, clienteId, sistemas.length, initialInformeId]);

  const loadInforme = async (id: string, disponibles?: Record<string, ChecklistResumen[]>) => {
    const { data, error } = await supabase
      .from('informes_consolidados')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return;
    setInformeId(data.id);
    setEstado(data.estado as any);
    const cat = ((data as any).categoria as 'seguridad' | 'pci' | undefined) || 'seguridad';
    setCategoria(cat);
    setSelectedSistemaIds(new Set(data.sistemas_ids || []));
    setFirmaTecnico(data.firma_tecnico || '');
    setFirmaCliente(data.firma_cliente || '');
    setNombreFirmanteCliente(data.nombre_firmante_cliente || '');
    setObservacionesGenerales(data.observaciones_generales || '');

    // Restaurar la selección concreta de checklists si está guardada
    const guardados: string[] = (data as any).checklists_ids || [];
    if (guardados.length > 0) {
      const disp = disponibles || checklistsDisponiblesPorSistema;
      setChecklistsPorSistema(prev => {
        const next = { ...prev };
        Object.entries(disp).forEach(([sid, lista]) => {
          const elegido = lista.find(c => guardados.includes(c.id));
          if (elegido) next[sid] = elegido;
        });
        return next;
      });
    }
  };

  // ------ Cargar elementos e inventario probado de los sistemas seleccionados ------
  const cargarDatosSistemas = async () => {
    const ids = Array.from(selectedSistemaIds);
    if (ids.length === 0) return;

    const { data: elementos } = await supabase
      .from('elementos')
      .select('id, nombre, tipo, zona, revisado, sistema_id, observaciones, numero_elemento, estado, fecha_instalacion, tipo_equipo, marca, modelo, vr, area_particion, mac_serie, codigo_instalador, anio_fabricacion, datos_tecnicos')
      .in('sistema_id', ids);

    const elMap: Record<string, ElementoMin[]> = {};
    ids.forEach(id => { elMap[id] = []; });
    (elementos || []).forEach((e: any) => { elMap[e.sistema_id]?.push(e); });
    setElementosPorSistema(elMap);

    // Recopilar IDs de los checklists (Q&A) y los de revisión de elementos por sistema
    const checklistQAporSistema: Record<string, string> = {};
    const checklistRevPorSistema: Record<string, string> = {};
    ids.forEach(sid => {
      const qa = checklistsPorSistema[sid]?.id;
      const rev = revisionElementosPorSistema[sid]?.id;
      if (qa) checklistQAporSistema[sid] = qa;
      if (rev) checklistRevPorSistema[sid] = rev;
    });
    const allChecklistIds = Array.from(new Set([
      ...Object.values(checklistQAporSistema),
      ...Object.values(checklistRevPorSistema),
    ]));

    const probadosMap: Record<string, Set<string>> = {};
    ids.forEach(id => { probadosMap[id] = new Set(); });
    const obsElemMap: Record<string, string> = {};

    if (allChecklistIds.length > 0) {
      const { data: probados } = await supabase
        .from('checklist_elementos_probados')
        .select('elemento_id, checklist_id, resultado, observaciones')
        .in('checklist_id', allChecklistIds);

      const checklistToSistema: Record<string, string> = {};
      Object.entries(checklistQAporSistema).forEach(([sid, cid]) => { checklistToSistema[cid] = sid; });
      Object.entries(checklistRevPorSistema).forEach(([sid, cid]) => { checklistToSistema[cid] = sid; });

      (probados || []).forEach((p: any) => {
        const sid = checklistToSistema[p.checklist_id];
        if (sid) probadosMap[sid].add(p.elemento_id);
        if (p.observaciones && p.observaciones.trim()) {
          obsElemMap[p.elemento_id] = p.observaciones.trim();
        }
      });
    }
    setElementosProbadosPorSistema(probadosMap);
    setObservacionesElemento(obsElemMap);

    // Observaciones generales por sistema (de los checklists asociados)
    const obsSistMap: Record<string, string[]> = {};
    ids.forEach(id => { obsSistMap[id] = []; });
    if (allChecklistIds.length > 0) {
      const { data: chs } = await supabase
        .from('checklists_seguridad')
        .select('id, sistema_id, observaciones_generales')
        .in('id', allChecklistIds);
      (chs || []).forEach((c: any) => {
        if (c.observaciones_generales && c.observaciones_generales.trim() && obsSistMap[c.sistema_id]) {
          obsSistMap[c.sistema_id].push(...extractReadableObservaciones(c.observaciones_generales));
        }
      });
    }
    setObservacionesSistema(obsSistMap);

    // Anomalías abiertas por sistema (vía elemento_id)
    const todosElementoIds = (elementos || []).map((e: any) => e.id);
    const anomMap: Record<string, AnomaliaRow[]> = {};
    ids.forEach(id => { anomMap[id] = []; });
    const codigosUsados = new Set<string>();

    if (todosElementoIds.length > 0) {
      const { data: anomalias } = await supabase
        .from('anomalias_elementos')
        .select('id, elemento_id, codigo_anomalia, descripcion, notas_resolucion, resuelta')
        .in('elemento_id', todosElementoIds)
        .eq('resuelta', false);

      const elementoToSistema: Record<string, string> = {};
      (elementos || []).forEach((e: any) => { elementoToSistema[e.id] = e.sistema_id; });

      (anomalias || []).forEach((a: any) => {
        const sid = elementoToSistema[a.elemento_id];
        if (sid && anomMap[sid]) anomMap[sid].push(a as AnomaliaRow);
        if (a.codigo_anomalia) codigosUsados.add(a.codigo_anomalia);
      });
    }
    setAnomaliasPorSistema(anomMap);

    // Cargar info de los códigos usados para la leyenda
    const leyMap: Record<string, CodigoAnomaliaInfo> = {};
    if (codigosUsados.size > 0) {
      const { data: codigos } = await supabase
        .from('codigos_anomalias')
        .select('codigo, descripcion, severidad, solucion_sugerida, referencia_normativa, justificacion_normativa')
        .in('codigo', Array.from(codigosUsados));
      (codigos || []).forEach((c: any) => { leyMap[c.codigo] = c as CodigoAnomaliaInfo; });
      // Asegurar que todos los códigos están presentes aunque no estén en catálogo
      codigosUsados.forEach(cod => {
        if (!leyMap[cod]) leyMap[cod] = { codigo: cod, descripcion: null, severidad: null, solucion_sugerida: null, referencia_normativa: null, justificacion_normativa: null };
      });
    }
    setLeyendaCodigos(leyMap);
  };

  // ------ Persistencia ------
  const upsertInforme = async (nuevoEstado: 'borrador' | 'finalizado'): Promise<string | null> => {
    if (!clienteId || !perfil?.id || !empresa?.id) {
      toast({ title: 'Faltan datos', description: 'No se pudo identificar el usuario o empresa.', variant: 'destructive' });
      return null;
    }
    const checklistsElegidos = Array.from(selectedSistemaIds)
      .map(sid => checklistsPorSistema[sid]?.id)
      .filter(Boolean) as string[];
    const payload: any = {
      empresa_id: empresa.id,
      cliente_id: clienteId,
      sede_id: sede?.id || null,
      tecnico_id: perfil.id,
      sistemas_ids: Array.from(selectedSistemaIds),
      checklists_ids: checklistsElegidos,
      estado: nuevoEstado,
      categoria,
      firma_tecnico: firmaTecnico || null,
      firma_cliente: firmaCliente || null,
      nombre_firmante_cliente: nombreFirmanteCliente || null,
      observaciones_generales: observacionesGenerales || null,
    };
    if (informeId) {
      const { error } = await supabase.from('informes_consolidados').update(payload).eq('id', informeId);
      if (error) { toast({ title: 'Error al guardar', description: error.message, variant: 'destructive' }); return null; }
      return informeId;
    }
    const { data, error } = await supabase.from('informes_consolidados').insert(payload).select('id').single();
    if (error) { toast({ title: 'Error al guardar', description: error.message, variant: 'destructive' }); return null; }
    setInformeId(data.id);
    return data.id;
  };

  const capturarFirmas = () => {
    let tec = firmaTecnico;
    let cli = firmaCliente;
    if (sigTecnicoRef.current && !sigTecnicoRef.current.isEmpty()) {
      tec = sigTecnicoRef.current.getTrimmedCanvas().toDataURL('image/png');
      setFirmaTecnico(tec);
    }
    if (sigClienteRef.current && !sigClienteRef.current.isEmpty()) {
      cli = sigClienteRef.current.getTrimmedCanvas().toDataURL('image/png');
      setFirmaCliente(cli);
    }
    return { tec, cli };
  };

  const handleGuardarBorrador = async () => {
    capturarFirmas();
    setSaving(true);
    const id = await upsertInforme('borrador');
    setSaving(false);
    if (id) {
      toast({ title: 'Borrador guardado', description: 'Podrás retomarlo más adelante.' });
      onOpenChange(false);
    }
  };

  const handleSiguienteDesdeSelect = async () => {
    if (selectedSistemaIds.size === 0) return;
    setLoading(true);
    await cargarDatosSistemas();
    setLoading(false);
    // Crear/actualizar borrador para tener id antes de firmas
    await upsertInforme('borrador');
    setStep('sign');
  };

  const handleSiguienteDesdeSign = () => {
    const { tec, cli } = capturarFirmas();
    if (!tec) { toast({ title: 'Falta la firma del técnico', variant: 'destructive' }); return; }
    if (!cli) { toast({ title: 'Falta la firma del cliente', variant: 'destructive' }); return; }
    if (!nombreFirmanteCliente.trim()) { toast({ title: 'Indica el nombre del firmante (cliente)', variant: 'destructive' }); return; }
    setStep('preview');
  };

  const handleFinalizar = async () => {
    setGenerating(true);
    // Primero se guarda/actualiza como borrador. Solo se marca como finalizado
    // cuando el PDF ya está generado, subido y vinculado en la ficha.
    const id = await upsertInforme('borrador');
    if (!id) { setGenerating(false); return; }
    try {
      const { blob, fileName } = await generatePDF();
      // Subir a Storage
      const path = `${empresa?.id || 'empresa'}/${clienteId || 'cliente'}/${id}.pdf`;
      const { error: upErr } = await supabase.storage
        .from('informes-consolidados')
        .upload(path, blob, { contentType: 'application/pdf', upsert: true });
      if (upErr) throw upErr;
      const { error: updateErr } = await supabase
        .from('informes_consolidados')
        .update({ estado: 'finalizado', pdf_path: path })
        .eq('id', id);
      if (updateErr) throw updateErr;
      setEstado('finalizado');
      // Descarga inmediata para el técnico
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Informe finalizado', description: 'PDF generado y almacenado correctamente.' });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: 'Error al generar PDF', description: e?.message || 'Revisa la consola.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleEliminarBorrador = async (id: string) => {
    const { error } = await supabase.from('informes_consolidados').delete().eq('id', id);
    if (!error) {
      setBorradoresExistentes(prev => prev.filter(b => b.id !== id));
      if (id === informeId) reset();
      toast({ title: 'Borrador eliminado' });
    }
  };

  // ------ PDF ------
  const generatePDF = async () => {
    const empresaInfo: EmpresaInfo | null = empresa ? {
      nombre: empresa.nombre,
      razon_social: empresa.razon_social,
      nif_cif: empresa.nif_cif,
      telefono: empresa.telefono,
      email: empresa.email,
      logo_url: empresa.logo_url,
      sello_url: (empresa as any).sello_url,
      numero_dgp: categoria === 'pci' ? null : (empresa as any).numero_dgp,
      numero_reci: categoria === 'pci' ? (empresa as any).numero_reci : null,
      direccion_calle: (empresa as any).direccion_calle,
      direccion_numero: (empresa as any).direccion_numero,
      codigo_postal: (empresa as any).codigo_postal,
      ciudad: (empresa as any).ciudad,
      provincia: (empresa as any).provincia,
    } : null;


    const builder = await PdfDocumentBuilder.create(empresaInfo, docConfig);

    builder.addBrandedHeader({
      titulo: `Informe de Revisión — ${CATEGORIA_LABEL[categoria]}`,
      estado: `${selectedSistemas.length} sistema${selectedSistemas.length === 1 ? '' : 's'}`,
      fechaEmision: `Emitido: ${new Date().toLocaleDateString('es-ES')}`,
    });

    const today = format(new Date(), "dd 'de' MMMM yyyy", { locale: es });
    const emplazamiento = sede
      ? `${sede.nombre}${sede.direccion ? ` — ${sede.direccion}` : ''}${(sede.codigo_postal || sede.poblacion) ? `, ${[sede.codigo_postal, sede.poblacion].filter(Boolean).join(' ')}` : ''}${sede.provincia ? ` (${sede.provincia})` : ''}`
      : '-';

    const dirCliente = [
      (cliente as any)?.direccion_fiscal || (cliente as any)?.direccion,
      [(cliente as any)?.codigo_postal, (cliente as any)?.poblacion].filter(Boolean).join(' '),
    ].filter(Boolean).join(' · ');

    const clienteFields: any[] = [
      { label: 'Titular', value: (cliente as any)?.nombre_fiscal || cliente?.nombre || clienteNombre || '—' },
      { label: 'NIF/CIF', value: (cliente as any)?.nif_cif || (cliente as any)?.dni_nif || '—' },
    ];
    if (dirCliente) clienteFields.push({ label: 'Domicilio', value: dirCliente });
    if ((cliente as any)?.email) clienteFields.push({ label: 'Email', value: (cliente as any).email });
    if ((cliente as any)?.telefono) clienteFields.push({ label: 'Teléfono', value: (cliente as any).telefono });
    if (sede) clienteFields.push({ label: 'Emplazamiento', value: emplazamiento });

    const empresaFields: any[] = [
      { label: 'Razón social', value: (empresa as any)?.razon_social || empresa?.nombre || '—' },
      { label: 'NIF', value: (empresa as any)?.nif_cif || '—' },
    ];
    if (categoria === 'pci') {
      if ((empresa as any)?.numero_reci) empresaFields.push({ label: 'Nº RECI', value: (empresa as any).numero_reci });
    } else {
      if ((empresa as any)?.numero_dgp) empresaFields.push({ label: 'Nº DGP', value: (empresa as any).numero_dgp });
    }

    builder.addInfoCardRow(
      { title: 'Titular de la instalación', fields: clienteFields },
      { title: 'Mantenedor autorizado', fields: empresaFields },
    );

    builder.addSection('Datos del informe');
    builder.addInfoCard('Detalle', [
      { label: 'Técnico', value: perfil?.nombre || '—' },
      { label: 'Fecha', value: today },
      { label: 'Sistemas incluidos', value: String(selectedSistemas.length) },
    ]);



    if (observacionesGenerales) {
      builder.addSection('Observaciones generales');
      builder.addQuoteBlock(observacionesGenerales);
    }

    // Por cada sistema: cabecera + checklist (preguntas y respuestas guardadas) + inventario marcando probados
    for (let i = 0; i < selectedSistemas.length; i++) {
      const sistema = selectedSistemas[i];
      builder.addPage();
      builder.addSection(`Sistema: ${getSystemTypeLabel(sistema.tipo)}`);

      const checklist = checklistsPorSistema[sistema.id];
      const els = elementosPorSistema[sistema.id] || [];
      const probados = elementosProbadosPorSistema[sistema.id] || new Set<string>();
      const instrumentosSistema = resolveInstrumentosGrupoPresion(els);

      const sistemaFields = [
        ...(sistema.ubicacion ? [{ label: 'Ubicación', value: sistema.ubicacion }] : []),
        { label: 'Checklist', value: checklist ? (checklist.estado === 'completado' ? 'Completado' : 'En curso') : 'No realizado' },
        { label: 'Tipo revisión', value: checklist?.tipo_revision || '-' },
        { label: 'Elementos probados', value: `${probados.size}/${els.length}` },
      ];
      builder.addInfoCard('Resumen del sistema', sistemaFields);

      // Checklist Q&A guardado
      if (checklist) {
        const { data: respuestas } = await supabase
          .from('checklist_respuestas')
          .select('bloque_id, pregunta_texto, respuesta, observaciones')
          .eq('checklist_id', checklist.id)
          .order('bloque_id');

        if (respuestas && respuestas.length > 0) {
          builder.addSection('Checklist');
          const porBloque: Record<string, any[]> = {};
          respuestas.forEach((r: any) => {
            (porBloque[r.bloque_id] = porBloque[r.bloque_id] || []).push(r);
          });
          Object.entries(porBloque).forEach(([bloque, items]) => {
            builder.addBlockSubtitle(bloque);
            items.forEach((it: any) => {
              const r = (it.respuesta || '').toLowerCase();
              const label = r === 'si' ? 'SÍ' : r === 'no' ? 'NO' : r === 'na' ? 'N/A' : (it.respuesta ? String(it.respuesta).toUpperCase() : '—');
              const tone: 'ok' | 'ko' | 'na' | 'neutral' =
                r === 'si' ? 'ok' : r === 'no' ? 'ko' : r === 'na' ? 'na' : 'neutral';
              builder.addChecklistItem({
                text: it.pregunta_texto || '',
                badge: label,
                badgeTone: tone,
                observaciones: it.observaciones || undefined,
              });
            });
            builder.addSpacer(2);
          });
        }
      }

      // Prueba de prestaciones (curva caudal/presión) — solo grupos de presión
      if (checklist && sistema.tipo === 'pci_grupo_presion') {
        const { data: cabs } = await (supabase as any)
          .from('checklist_cabeceras')
          .select('protocolo_id, datos')
          .eq('checklist_id', checklist.id)
          .eq('protocolo_id', 'prueba_prestaciones');
        const datos = (cabs && cabs[0]?.datos) as any;
        // Normaliza estructura (nueva = { bombas: {...} }, legacy = { bomba_electrica, bomba_diesel })
        const bombasArr: Array<{ nombre: string; tipo: 'electrica' | 'diesel'; data: any }> = [];
        if (datos?.bombas && typeof datos.bombas === 'object') {
          Object.values(datos.bombas as Record<string, any>).forEach((b: any) => {
            if (b) bombasArr.push({ nombre: b.nombre || 'Bomba', tipo: b.tipo || 'electrica', data: b });
          });
        } else if (datos) {
          if (datos.bomba_electrica) bombasArr.push({ nombre: 'Bomba principal eléctrica', tipo: 'electrica', data: datos.bomba_electrica });
          if (datos.bomba_diesel) bombasArr.push({ nombre: 'Bomba principal diésel', tipo: 'diesel', data: datos.bomba_diesel });
        }
        // Ordena: eléctricas primero, luego diésel, alfabético por nombre.
        bombasArr.sort((a, b) => (a.tipo === b.tipo ? a.nombre.localeCompare(b.nombre) : a.tipo === 'electrica' ? -1 : 1));
        const bombasConDatos = bombasArr.filter(b => {
          const d = b.data;
          return d && (d.puntos?.some((p: any) => p.presion || p.caudal || p.presion_bar || p.caudal_lpm) || d.resultado);
        });
        if (bombasConDatos.length > 0) {
          // Etiquetas de unidades por bomba (con fallback a la legacy l/min · bar).
          const { labelCaudal, labelPresion } = await import('@/lib/checklists/units');
          builder.addSection('Prueba de prestaciones — Curva caudal/presión');
          for (const b of bombasConDatos) {
            const d = b.data;
            const uCaudalInstr = instrumentosSistema.caudalimetro?.unidad || d?.unidades?.instrumento?.caudal || 'lpm';
            const uPresionInstr = instrumentosSistema.manometro?.unidad || d?.unidades?.instrumento?.presion || 'bar';
            const uCaudalPlaca = d?.unidades?.placa?.caudal || 'lpm';
            const uPresionPlaca = d?.unidades?.placa?.presion || 'bar';
            const lblCInstr = labelCaudal(uCaudalInstr);
            const lblPInstr = labelPresion(uPresionInstr);
            const lblCPlaca = labelCaudal(uCaudalPlaca);
            const lblPPlaca = labelPresion(uPresionPlaca);
            const tipoLabel = b.tipo === 'electrica' ? 'eléctrica' : 'diésel';
            builder.addBlockSubtitle(`${b.nombre} (${tipoLabel})`);
            const nominalCaudal = Number(d?.nominal?.caudal ?? d?.nominal?.caudal_lpm ?? 0) || 0;
            const nominalPresion = Number(d?.nominal?.presion ?? d?.nominal?.presion_bar ?? 0) || 0;
            builder.addText(
              `Placa: ${nominalCaudal} ${lblCPlaca} @ ${nominalPresion} ${lblPPlaca} — Instrumentos: caudalímetro ${lblCInstr}, manómetro ${lblPInstr}`,
              10,
            );
            const cw = builder.contentWidth;
            const colW = [cw * 0.18, cw * 0.41, cw * 0.41];
            const rows = (d.puntos || []).map((p: any) => [
              `${p.pct}%`,
              (p.caudal ?? p.caudal_lpm) != null ? String(p.caudal ?? p.caudal_lpm) : '—',
              (p.presion ?? p.presion_bar) != null ? String(p.presion ?? p.presion_bar) : '—',
            ]);
            builder.addMultilineTable(
              ['% caudal', `Caudalímetro (${lblCInstr})`, `Manómetro (${lblPInstr})`],
              rows,
              colW,
            );

            // Gráfica
            try {
              const puntosNorm = (d.puntos || []).map((p: any) => ({
                pct: Number(p.pct) || 0,
                caudal: Number(p.caudal ?? p.caudal_lpm ?? 0) || 0,
                presion: Number(p.presion ?? p.presion_bar ?? 0) || 0,
              }));
              const url = renderCurvaCaudalToDataURL({
                nominal: { caudal: nominalCaudal, presion: nominalPresion },
                puntos: puntosNorm,
                ancho: 1000,
                alto: 500,
                titulo: b.nombre,
                unidadCaudalLabel: lblCInstr,
                unidadPresionLabel: lblPInstr,
                unidadCaudalPlaca: uCaudalPlaca,
                unidadPresionPlaca: uPresionPlaca,
                unidadCaudalInstrumento: uCaudalInstr,
                unidadPresionInstrumento: uPresionInstr,
              });
              if (url) {
                const imgW = builder.contentWidth;
                const imgH = imgW * 0.5;
                builder.addPageBreakIfNeeded(imgH + 12);
                builder.pdf.addImage(url, 'PNG', 15, builder.currentY, imgW, imgH);
                builder.currentY = builder.currentY + imgH + 4;
              }
            } catch (e) { console.error('curva chart', e); }

            const res = d.resultado;
            if (res === 'positivo') {
              builder.addText('Resultado: POSITIVO ✓', 11);
            } else if (res === 'negativo') {
              builder.addText('Resultado: NEGATIVO ✗', 11);
            } else {
              builder.addText('Resultado: pendiente de validación', 10);
            }

            // Alertas automáticas UNE 23500 / instalación
            try {
              const { evaluarAlertasBomba } = await import('@/lib/checklists/pruebaPrestacionesAlertas');
              // Construye un PruebaBombaData válido a partir del JSON crudo.
              const dataNorm: any = {
                elemento_id: d.elemento_id || b.nombre,
                nombre: b.nombre,
                tipo: b.tipo,
                unidades: {
                  placa: { caudal: uCaudalPlaca, presion: uPresionPlaca },
                  instrumento: { caudal: uCaudalInstr, presion: uPresionInstr },
                },
                nominal: { caudal: nominalCaudal, presion: nominalPresion },
                puntos: (d.puntos || []).map((p: any) => ({
                  pct: Number(p.pct) || 0,
                  caudal: Number(p.caudal ?? p.caudal_lpm ?? 0) || 0,
                  presion: Number(p.presion ?? p.presion_bar ?? 0) || 0,
                })),
                resultado: d.resultado ?? null,
                observaciones: d.observaciones || '',
              };
              const alertas = evaluarAlertasBomba(dataNorm);
              const anomalias = alertas.filter(a => a.destinoPDF === 'anomalia');
              const avisos = alertas.filter(a => a.destinoPDF === 'alerta');
              if (anomalias.length > 0) {
                builder.addText('Anomalías UNE 23500:', 11);
                anomalias.forEach(a => {
                  builder.addQuoteBlock(`${a.icono} ${a.mensaje}${a.pct != null ? ` (punto ${a.pct} %)` : ''}`);
                });
              }
              if (avisos.length > 0) {
                builder.addText('Alertas de instalación:', 11);
                avisos.forEach(a => {
                  builder.addQuoteBlock(`${a.icono} ${a.mensaje}${a.pct != null ? ` (punto ${a.pct} %)` : ''}`);
                });
              }
            } catch (e) { console.error('alertas curva', e); }

            if (d.observaciones) builder.addQuoteBlock(d.observaciones);
            builder.addSpacer(3);
          }
        }
      }


      // Observaciones generales del sistema (de los checklists) + códigos de anomalías detectados
      const obsSistRaw = observacionesSistema[sistema.id] || [];
      const obsSistUnique = Array.from(new Set(obsSistRaw.flatMap(extractReadableObservaciones).map(t => (t || '').trim()).filter(Boolean)));
      const anomaliasSistemaPrev = anomaliasPorSistema[sistema.id] || [];
      const codigosUnicos = Array.from(new Set(
        anomaliasSistemaPrev
          .map(a => a.codigo_anomalia)
          .filter((c): c is string => !!c && c.trim().length > 0)
      )).sort();
      if (obsSistUnique.length > 0 || codigosUnicos.length > 0) {
        builder.addSection('Observaciones generales del sistema');
        obsSistUnique.forEach(txt => {
          builder.addQuoteBlock(txt);
        });
        if (codigosUnicos.length > 0) {
          const codigosTxt = codigosUnicos
            .map(c => {
              const info = leyendaCodigos[c];
              return info?.descripcion ? `${c} — ${info.descripcion}` : c;
            })
            .join(' · ');
          builder.addQuoteBlock(`Códigos de anomalías detectados: ${codigosTxt}`);
        }
        builder.addSpacer(2);
      }


      // Resumen de incidencias por código (agrupado)
      const anomaliasSistema = anomaliasPorSistema[sistema.id] || [];
      if (anomaliasSistema.length > 0) {
        const conteo: Record<string, number> = {};
        anomaliasSistema.forEach(a => {
          const c = a.codigo_anomalia || 'SIN_CODIGO';
          conteo[c] = (conteo[c] || 0) + 1;
        });
        builder.addSection('Resumen de incidencias');
        const cw = builder.contentWidth;
        const colWRes = [cw * 0.14, cw * 0.58, cw * 0.16, cw * 0.12];
        const resRows = Object.entries(conteo)
          .sort((a, b) => b[1] - a[1])
          .map(([cod, count]) => {
            const info = leyendaCodigos[cod];
            return [
              cod,
              info?.descripcion || '—',
              info?.severidad || '—',
              String(count),
            ];
          });
        builder.addMultilineTable(['Código', 'Descripción', 'Severidad', 'Nº elementos'], resRows, colWRes);
        builder.addSpacer(3);
      }

      // Observaciones por elemento: Ubicación, Tipo, Anomalía
      // (el detalle del código se consulta en la leyenda)
      const elementoById: Record<string, ElementoMin> = {};
      els.forEach(e => { elementoById[e.id] = e; });
      const obsRows: string[][] = [];
      const elementosConFila = new Set<string>();
      const ubicacionFor = (el: ElementoMin) => {
        const partes = [el.numero_elemento ? `#${el.numero_elemento}` : '', el.nombre, el.zona].filter(Boolean);
        return partes.join(' · ');
      };

      // 1) Anomalías codificadas
      anomaliasSistema.forEach(a => {
        const el = elementoById[a.elemento_id];
        if (!el) return;
        elementosConFila.add(a.elemento_id);
        const info = leyendaCodigos[a.codigo_anomalia || ''];
        const detalle = [a.descripcion, a.notas_resolucion, observacionesElemento[a.elemento_id]]
          .filter(Boolean).join(' — ');
        const codigoTxt = a.codigo_anomalia
          ? `${a.codigo_anomalia}${info?.descripcion ? ` — ${info.descripcion}` : ''}`
          : '—';
        const anomaliaTxt = detalle ? `${codigoTxt}\n${detalle}` : codigoTxt;
        obsRows.push([
          ubicacionFor(el),
          el.tipo || '—',
          anomaliaTxt,
        ]);
      });
      // 2) Elementos con observación en el checklist pero sin anomalía codificada
      els.forEach(el => {
        if (elementosConFila.has(el.id)) return;
        const obsCk = observacionesElemento[el.id];
        const obsEl = el.observaciones && el.observaciones.trim() ? el.observaciones.trim() : '';
        const texto = [obsCk, obsEl].filter(Boolean).join(' — ');
        if (texto) {
          obsRows.push([ubicacionFor(el), el.tipo || '—', texto]);
        }
      });
      if (obsRows.length > 0) {
        builder.addSection('Observaciones por elemento');
        const cw = builder.contentWidth;
        const colWObs = [cw * 0.32, cw * 0.18, cw * 0.50];
        builder.addMultilineTable(
          ['Ubicación', 'Tipo de elemento', 'Anomalía'],
          obsRows,
          colWObs,
        );
        builder.addText('Consulte la leyenda de códigos de anomalía al final del informe para ver la justificación, referencia normativa y solución sugerida.', 8);
        builder.addSpacer(2);
      }




      // Inventario revisado — tabla horizontal (landscape) con columnas dinámicas y cabeceras abreviadas
      const anomaliaElementoIds = new Set(anomaliasSistema.map(a => a.elemento_id));

      const resultadoFor = (el: ElementoMin): string => {
        const probado = probados.has(el.id);
        if (!probado) return 'Pendiente';
        if (anomaliaElementoIds.has(el.id)) return 'ANOMALÍA';
        return 'OK';
      };

      const fmtVal = (v: any): string => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return v.trim();
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
        return String(v).trim();
      };

      // Mapa de abreviaturas para cabeceras (key DB -> etiqueta corta en PDF)
      const headerAbbr: Record<string, string> = {
        fecha_instalacion: 'F. Inst',
        fecha_caducidad: 'F. Caduc',
        fecha_fabricacion: 'F. Fab',
        prox_fecha_retimbre: 'P. Retimb',
        proxima_fecha_retimbre: 'P. Retimb',
        ultimo_retimbre: 'Últ. Ret',
        ultima_revision: 'Últ. Rev',
        area_particion: 'Área',
        anio_fabricacion: 'Año Fab',
        codigo_instalador: 'Cód. Inst',
        mac_serie: 'S/N',
        tipo_equipo: 'T. Equipo',
        numero_elemento: 'Nº',
        observaciones: 'Obs.',
      };
      const abbrHeader = (raw: string): string => {
        if (headerAbbr[raw]) return headerAbbr[raw];
        // prettify + abreviar palabras largas habituales
        return raw
          .replace(/[_-]+/g, ' ')
          .replace(/\bfecha\b/gi, 'F.')
          .replace(/\bnumero\b/gi, 'Nº')
          .replace(/\bcodigo\b/gi, 'Cód.')
          .replace(/\bcapacidad\b/gi, 'Cap.')
          .replace(/\beficacia\b/gi, 'Efic.')
          .replace(/\bobservaciones\b/gi, 'Obs.')
          .replace(/\b\w/g, c => c.toUpperCase());
      };

      type Col = { key: string; header: string; weight: number; getter: (el: ElementoMin) => string };
      const baseCols: Col[] = [
        { key: 'num', header: 'Nº', weight: 0.5, getter: el => el.numero_elemento != null ? String(el.numero_elemento) : '' },
        { key: 'nombre', header: 'Elemento', weight: 1.8, getter: el => fmtVal(el.nombre) },
        { key: 'tipo', header: 'Tipo', weight: 1.4, getter: el => fmtVal(el.tipo) },
        { key: 'zona', header: 'Zona', weight: 1.1, getter: el => fmtVal(el.zona) },
        { key: 'area_particion', header: abbrHeader('area_particion'), weight: 0.8, getter: el => fmtVal(el.area_particion) },
        { key: 'marca', header: 'Marca', weight: 1.0, getter: el => fmtVal(el.marca) },
        { key: 'modelo', header: 'Modelo', weight: 1.0, getter: el => fmtVal(el.modelo) },
        { key: 'vr', header: 'VR', weight: 0.5, getter: el => fmtVal(el.vr) },
        { key: 'mac_serie', header: abbrHeader('mac_serie'), weight: 1.1, getter: el => fmtVal(el.mac_serie) },
        { key: 'codigo_instalador', header: abbrHeader('codigo_instalador'), weight: 0.9, getter: el => fmtVal(el.codigo_instalador) },
        { key: 'anio_fabricacion', header: abbrHeader('anio_fabricacion'), weight: 0.6, getter: el => fmtVal(el.anio_fabricacion) },
        { key: 'fecha_instalacion', header: abbrHeader('fecha_instalacion'), weight: 0.8, getter: el => fmtVal(el.fecha_instalacion) },
        { key: 'tipo_equipo', header: abbrHeader('tipo_equipo'), weight: 1.0, getter: el => fmtVal(el.tipo_equipo) },
        { key: 'estado', header: 'Estado', weight: 0.7, getter: el => fmtVal(el.estado) },
        { key: 'observaciones', header: abbrHeader('observaciones'), weight: 1.4, getter: el => fmtVal(el.observaciones) },
        { key: 'resultado', header: 'Resultado', weight: 0.9, getter: el => resultadoFor(el) },
      ];

      // Claves dinámicas presentes en datos_tecnicos
      const dtKeys = new Set<string>();
      els.forEach(el => {
        const dt = el.datos_tecnicos;
        if (dt && typeof dt === 'object') {
          Object.entries(dt).forEach(([k, v]) => {
            if (fmtVal(v).length > 0) dtKeys.add(k);
          });
        }
      });
      const dtCols: Col[] = Array.from(dtKeys).sort().map(k => ({
        key: `dt:${k}`,
        header: abbrHeader(k),
        weight: 0.9,
        getter: (el: ElementoMin) => {
          const dt = el.datos_tecnicos;
          if (!dt || typeof dt !== 'object') return '';
          return fmtVal((dt as any)[k]);
        },
      }));

      // Insertar dt antes de la columna Resultado
      const candidates = [...baseCols.slice(0, baseCols.length - 1), ...dtCols, baseCols[baseCols.length - 1]];

      // Filtrar columnas vacías. Mantener siempre Nº, Elemento y Resultado.
      const alwaysKeep = new Set(['num', 'nombre', 'resultado']);
      const activeCols = candidates.filter(col => {
        if (alwaysKeep.has(col.key)) return true;
        return els.some(el => col.getter(el).length > 0);
      });

      // Salto a landscape si hay muchas columnas
      const goLandscape = activeCols.length > 6;
      if (goLandscape) builder.addLandscapePage();

      builder.addSection(`Inventario revisado — ${getSystemTypeLabel(sistema.tipo)}`);

      const cw = builder.contentWidth;
      const totalWeight = activeCols.reduce((acc, c) => acc + c.weight, 0);
      const colW = activeCols.map(c => (cw * c.weight) / totalWeight);

      const rows = els.map(el =>
        activeCols.map(c => {
          const v = c.getter(el);
          return v.length > 0 ? v : '-';
        }),
      );

      builder.addMultilineTable(
        activeCols.map(c => c.header),
        rows,
        colW,
        {
          fontSize: goLandscape ? 6.5 : 8,
          cellPadX: 1,
          cellPadY: 1,
        },
      );

      if (goLandscape) builder.addPortraitPage();
    }



    // Leyenda de códigos de anomalía utilizados (landscape: 6 columnas)
    const codigosUsados = Object.keys(leyendaCodigos);
    if (codigosUsados.length > 0) {
      builder.addPage();
      builder.addSection('Leyenda de códigos de anomalía');
      const cw = builder.contentWidth;
      const colWLey = [cw * 0.10, cw * 0.22, cw * 0.08, cw * 0.24, cw * 0.12, cw * 0.24];
      const leyRows = codigosUsados.sort().map(cod => {
        const info = leyendaCodigos[cod];
        return [
          cod,
          info?.descripcion || '—',
          info?.severidad || '—',
          info?.justificacion_normativa || '—',
          info?.referencia_normativa || '—',
          info?.solucion_sugerida || '—',
        ];
      });
      builder.addMultilineTable(
        ['Código', 'Descripción', 'Severidad', 'Justificación normativa', 'Ref. normativa', 'Solución sugerida'],
        leyRows,
        colWLey,
      );
      builder.addSpacer(4);
    }

    // Firmas
    builder.addSignatureBlock([
      { label: 'Firma del técnico', imageData: firmaTecnico || null, nombre: perfil?.nombre },
      { label: 'Sello de la empresa', esEmpresa: true },
      { label: `Firma del cliente${nombreFirmanteCliente ? ` (${nombreFirmanteCliente})` : ''}`, imageData: firmaCliente || null, nombre: nombreFirmanteCliente },
    ]);

    const fileName = `informe_revision_${(clienteNombre || 'cliente').replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
    const pdf = builder.finalize();
    const blob = pdf.output('blob') as Blob;
    return { blob, fileName };
  };

  // ------ Reset al cerrar ------
  useEffect(() => { if (!open) reset(); }, [open]);

  // ------ Render helpers ------
  const sistemaStatus = (sistemaId: string): { label: string; tone: 'ok' | 'warn' | 'none' } => {
    const c = checklistsPorSistema[sistemaId];
    if (!c) return { label: 'Sin checklist', tone: 'none' };
    if (c.estado === 'completado' || c.estado === 'finalizado') return { label: 'Checklist completado', tone: 'ok' };
    return { label: 'Checklist en curso', tone: 'warn' };
  };

  const elementosStatus = (sistemaId: string): { label: string; tone: 'ok' | 'warn' | 'none' } => {
    const r = revisionElementosPorSistema[sistemaId];
    if (!r) return { label: 'Sin revisión elementos', tone: 'none' };
    if (r.estado === 'finalizada' || r.estado === 'completado' || r.estado === 'finalizado') {
      return { label: 'Elementos revisados', tone: 'ok' };
    }
    return { label: 'Elementos en curso', tone: 'warn' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] h-[95vh] sm:h-auto sm:max-h-[90vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Informe {categoria === 'pci' ? 'PCI' : 'Seguridad'} - {clienteNombre || 'Cliente'}
            {estado === 'borrador' && informeId && (
              <Badge variant="outline" className="ml-2 bg-yellow-500/20 text-yellow-700 border-yellow-500/30">Borrador</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* PASO 1: SELECCIÓN DE SISTEMAS */}
        {!loading && step === 'select' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {borradoresExistentes.length > 0 && !informeId && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Borradores guardados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {borradoresExistentes.map(b => (
                    <div key={b.id} className="flex items-center justify-between p-2 rounded border text-sm">
                      <div>
                        <div className="font-medium">{format(new Date(b.fecha), "dd/MM/yyyy HH:mm")}</div>
                        <div className="text-xs text-muted-foreground">{b.sistemas_ids?.length || 0} sistemas</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => loadInforme(b.id)}>Reanudar</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEliminarBorrador(b.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Selecciona los sistemas y, para cada uno, qué checklist incluir en el informe.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={showFiltros ? 'secondary' : 'outline'}
                  onClick={() => setShowFiltros(v => !v)}
                  className="gap-1"
                >
                  <Filter className="h-4 w-4" />
                  Filtros
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedSistemaIds(prev => {
                      if (prev.size === sistemasCategoria.length) return new Set();
                      return new Set(sistemasCategoria.map(s => s.id));
                    });
                  }}
                >
                  <CheckSquare className="h-4 w-4 mr-1" />
                  {selectedSistemaIds.size === sistemasCategoria.length && sistemasCategoria.length > 0 ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
              </div>
            </div>

            {showFiltros && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Filtros para selección de checklists</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFiltroBusqueda('');
                        setFiltroEstado('todos');
                        setFiltroTipoRevision('todos');
                        setFiltroFechaDesde('');
                        setFiltroFechaHasta('');
                      }}
                      className="h-7 gap-1 text-xs"
                    >
                      <X className="h-3 w-3" /> Limpiar
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Buscar</Label>
                    <Input
                      placeholder="Texto en tipo de revisión..."
                      value={filtroBusqueda}
                      onChange={e => setFiltroBusqueda(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Estado</Label>
                    <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="completado">Completados</SelectItem>
                        <SelectItem value="en_progreso">En progreso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de revisión</Label>
                    <Select value={filtroTipoRevision} onValueChange={setFiltroTipoRevision}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {tiposRevisionDisponibles.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Desde</Label>
                      <Input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} className="h-8" />
                    </div>
                    <div>
                      <Label className="text-xs">Hasta</Label>
                      <Input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} className="h-8" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {sistemasCategoria.map(s => {
                const st = sistemaStatus(s.id);
                const stE = elementosStatus(s.id);
                const selected = selectedSistemaIds.has(s.id);
                const disponibles = checklistsDisponiblesPorSistema[s.id] || [];
                const filtrados = filtrarChecklists(disponibles);
                const elegidoId = checklistsPorSistema[s.id]?.id || '';
                const toggleSel = () => {
                  setSelectedSistemaIds(prev => {
                    const n = new Set(prev);
                    if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
                    return n;
                  });
                };
                return (
                  <div
                    key={s.id}
                    className={cn(
                      'rounded-lg border transition-colors',
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    )}
                  >
                    <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={toggleSel}>
                      <Checkbox checked={selected} onCheckedChange={toggleSel} onClick={e => e.stopPropagation()} />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{getSystemTypeLabel(s.tipo)}</div>
                        {s.ubicacion && <div className="text-xs text-muted-foreground">{s.ubicacion}</div>}
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        {st.tone === 'ok' && (
                          <Badge variant="outline" className="gap-1 bg-green-500/20 text-green-700 border-green-500/30">
                            <ClipboardCheck className="h-3 w-3" /> {st.label}
                          </Badge>
                        )}
                        {st.tone === 'warn' && (
                          <Badge variant="outline" className="gap-1 bg-yellow-500/20 text-yellow-700 border-yellow-500/30">
                            <ClipboardCheck className="h-3 w-3" /> {st.label}
                          </Badge>
                        )}
                        {st.tone === 'none' && (
                          <Badge variant="outline" className="gap-1 text-muted-foreground">
                            <ClipboardX className="h-3 w-3" /> {st.label}
                          </Badge>
                        )}
                        {stE.tone === 'ok' && (
                          <Badge variant="outline" className="gap-1 bg-green-500/20 text-green-700 border-green-500/30">
                            <CheckSquare className="h-3 w-3" /> {stE.label}
                          </Badge>
                        )}
                        {stE.tone === 'warn' && (
                          <Badge variant="outline" className="gap-1 bg-yellow-500/20 text-yellow-700 border-yellow-500/30">
                            <Search className="h-3 w-3" /> {stE.label}
                          </Badge>
                        )}
                        {stE.tone === 'none' && (
                          <Badge variant="outline" className="gap-1 text-muted-foreground">
                            <Search className="h-3 w-3" /> {stE.label}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {selected && (
                      <div className="px-3 pb-3 pt-0 border-t border-border/50 mt-1" onClick={e => e.stopPropagation()}>
                        <Label className="text-xs text-muted-foreground">
                          Checklist a incluir ({filtrados.length} de {disponibles.length} disponibles{disponibles.length !== filtrados.length ? ' tras filtros' : ''})
                        </Label>
                        {disponibles.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-1">Este sistema no tiene checklists. Se incluirá solo el inventario.</p>
                        ) : (
                          <Select
                            value={elegidoId || '__none__'}
                            onValueChange={(v) => {
                              setChecklistsPorSistema(prev => {
                                const next = { ...prev };
                                if (v === '__none__') {
                                  delete next[s.id];
                                } else {
                                  next[s.id] = disponibles.find(c => c.id === v);
                                }
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger className="h-8 mt-1">
                              <SelectValue placeholder="Selecciona un checklist" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Sin checklist (solo inventario)</SelectItem>
                              {(filtrados.length > 0 ? filtrados : disponibles).map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {format(new Date(c.fecha), "dd/MM/yyyy")} · {c.tipo_revision || 'revisión'} · {c.estado === 'completado' || c.estado === 'finalizado' ? 'Completado' : 'En curso'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PASO 2: FIRMAS */}
        {!loading && step === 'sign' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Observaciones generales del informe (opcional)</CardTitle></CardHeader>
              <CardContent>
                <Textarea
                  value={observacionesGenerales}
                  onChange={e => setObservacionesGenerales(e.target.value)}
                  placeholder="Notas que aplican al conjunto del informe..."
                  className="min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Firma del técnico ({perfil?.nombre || '—'})</CardTitle></CardHeader>
              <CardContent>
                <div className="border rounded-lg bg-white overflow-hidden">
                  <SignatureCanvas
                    ref={sigTecnicoRef}
                    penColor="black"
                    canvasProps={{ className: 'w-full h-32', style: { width: '100%', height: '128px' } }}
                  />
                </div>
                <div className="flex gap-2 mt-1">
                  <Button variant="ghost" size="sm" onClick={() => { sigTecnicoRef.current?.clear(); setFirmaTecnico(''); }}>Limpiar</Button>
                  {firmaTecnico && <span className="text-xs text-muted-foreground self-center">Firma guardada</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Firma del cliente</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label htmlFor="firmante">Nombre del firmante</Label>
                  <Input
                    id="firmante"
                    value={nombreFirmanteCliente}
                    onChange={e => setNombreFirmanteCliente(e.target.value)}
                    placeholder="Nombre y apellidos / cargo"
                  />
                </div>
                <div className="border rounded-lg bg-white overflow-hidden">
                  <SignatureCanvas
                    ref={sigClienteRef}
                    penColor="black"
                    canvasProps={{ className: 'w-full h-32', style: { width: '100%', height: '128px' } }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { sigClienteRef.current?.clear(); setFirmaCliente(''); }}>Limpiar</Button>
                  {firmaCliente && <span className="text-xs text-muted-foreground self-center">Firma guardada</span>}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PASO 3: VISTA PRELIMINAR */}
        {!loading && step === 'preview' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <Card>
              <CardContent className="pt-4 text-sm space-y-1">
                <p><span className="font-medium">Cliente:</span> {clienteNombre || '-'}</p>
                {sede && <p><span className="font-medium">Emplazamiento:</span> {sede.nombre}{sede.direccion ? ` — ${sede.direccion}` : ''}</p>}
                <p><span className="font-medium">Técnico:</span> {perfil?.nombre || '-'}</p>
                <p><span className="font-medium">Cliente firmante:</span> {nombreFirmanteCliente || '-'}</p>
                <p><span className="font-medium">Fecha:</span> {format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
                <p><span className="font-medium">Sistemas:</span> {selectedSistemas.length}</p>
              </CardContent>
            </Card>

            {selectedSistemas.map(s => {
              const c = checklistsPorSistema[s.id];
              const r = revisionElementosPorSistema[s.id];
              const els = elementosPorSistema[s.id] || [];
              const probados = elementosProbadosPorSistema[s.id] || new Set();
              const elementosOk = r && (r.estado === 'finalizada' || r.estado === 'completado' || r.estado === 'finalizado');
              return (
                <Card key={s.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
                      <span>{getSystemTypeLabel(s.tipo)}</span>
                      <div className="flex gap-1 flex-wrap">
                        {c?.estado === 'completado' || c?.estado === 'finalizado' ? (
                          <Badge className="bg-green-500/20 text-green-700 border-green-500/30" variant="outline">
                            <CheckCircle className="h-3 w-3 mr-1" />Checklist OK
                          </Badge>
                        ) : c ? (
                          <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30" variant="outline">
                            <AlertTriangle className="h-3 w-3 mr-1" />Checklist en curso
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Sin checklist</Badge>
                        )}
                        {elementosOk ? (
                          <Badge className="bg-green-500/20 text-green-700 border-green-500/30" variant="outline">
                            <CheckSquare className="h-3 w-3 mr-1" />Elementos OK
                          </Badge>
                        ) : r ? (
                          <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30" variant="outline">
                            <Search className="h-3 w-3 mr-1" />Elementos en curso
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Sin revisión elementos</Badge>
                        )}
                        <Badge variant="secondary">{probados.size}/{els.length} probados</Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                </Card>
              );
            })}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Firmas</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Técnico</p>
                  {firmaTecnico ? <img src={firmaTecnico} alt="firma técnico" className="border rounded bg-white h-20 object-contain w-full" /> : <p className="text-xs text-destructive">No firmado</p>}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                  {firmaCliente ? <img src={firmaCliente} alt="firma cliente" className="border rounded bg-white h-20 object-contain w-full" /> : <p className="text-xs text-destructive">No firmado</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Separator />

        {/* ACCIONES */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <div>
            {step !== 'select' && (
              <Button variant="ghost" onClick={() => setStep(step === 'preview' ? 'sign' : 'select')}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Atrás
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button variant="secondary" onClick={handleGuardarBorrador} disabled={saving || selectedSistemaIds.size === 0} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar borrador
            </Button>
            {step === 'select' && (
              <Button onClick={handleSiguienteDesdeSelect} disabled={selectedSistemaIds.size === 0} className="gap-2">
                Siguiente <ChevronRight className="h-4 w-4" />
              </Button>
            )}
            {step === 'sign' && (
              <Button onClick={handleSiguienteDesdeSign} className="gap-2">
                Vista previa <ChevronRight className="h-4 w-4" />
              </Button>
            )}
            {step === 'preview' && (
              <Button onClick={handleFinalizar} disabled={generating} className="gap-2">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Finalizar y descargar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
